// Physics snippets for the question editor's symbol palette.
// Each entry is [latex, description]; the description is what search matches on.
// Every snippet must render in KaTeX (admin preview and student site).

const physicsSymbols = [
  {
    title: "Common notation",
    symbols: [
      ["\\vec{F}", "Force vector"],
      ["\\vec{v}", "Velocity vector"],
      ["\\vec{a}", "Acceleration vector"],
      ["\\vec{r}", "Position vector"],
      ["\\hat{n}", "Unit normal"],
      ["|\\vec{v}|", "Magnitude"],
      ["\\Delta", "Change (Delta)"],
      ["\\delta", "Small change"],
      ["\\propto", "Proportional to"],
      ["\\approx", "Approximately"],
      ["\\sim", "Of the order of"],
      ["\\dot{x}", "Velocity (dot)"],
      ["\\ddot{x}", "Acceleration (double dot)"],
      ["\\langle x \\rangle", "Average value"],
      ["\\odot", "Out of the page"],
      ["\\otimes", "Into the page"],
      ["\\hbar", "Reduced Planck constant"],
      ["\\mathcal{E}", "EMF (script E)"],
      ["\\varepsilon", "EMF / permittivity"],
      ["\\Phi", "Flux"],
      ["\\Omega", "Ohm"],
      ["\\mho", "Mho (siemens)"],
      ["\\tau", "Torque / time constant"],
      ["\\omega", "Angular frequency"],
      ["\\lambda", "Wavelength"],
      ["\\nu", "Frequency"],
      ["\\rho", "Density / resistivity"],
      ["\\mu", "Coefficient / permeability"],
      ["\\eta", "Efficiency / viscosity"],
      ["\\theta", "Angle"],
      ["\\phi", "Phase"],
      ["^{\\circ}", "Degree"],
      ["\\parallel", "Parallel"],
      ["\\perp", "Perpendicular"],
    ],
  },
  {
    title: "Physical constants",
    symbols: [
      ["c = 3 \\times 10^{8}\\ \\text{m s}^{-1}", "Speed of light"],
      ["h = 6.63 \\times 10^{-34}\\ \\text{J s}", "Planck's constant"],
      ["\\hbar = \\frac{h}{2\\pi}", "Reduced Planck constant"],
      ["G = 6.67 \\times 10^{-11}\\ \\text{N m}^{2}\\text{kg}^{-2}", "Gravitational constant"],
      ["g = 9.8\\ \\text{m s}^{-2}", "Acceleration due to gravity"],
      ["e = 1.6 \\times 10^{-19}\\ \\text{C}", "Electron charge"],
      ["m_{e} = 9.1 \\times 10^{-31}\\ \\text{kg}", "Electron mass"],
      ["m_{p} = 1.67 \\times 10^{-27}\\ \\text{kg}", "Proton mass"],
      ["m_{n}", "Neutron mass"],
      ["k_{B} = 1.38 \\times 10^{-23}\\ \\text{J K}^{-1}", "Boltzmann constant"],
      ["N_{A} = 6.022 \\times 10^{23}\\ \\text{mol}^{-1}", "Avogadro's number"],
      ["R = 8.314\\ \\text{J mol}^{-1}\\text{K}^{-1}", "Gas constant"],
      ["\\varepsilon_{0} = 8.85 \\times 10^{-12}\\ \\text{C}^{2}\\text{N}^{-1}\\text{m}^{-2}", "Permittivity of free space"],
      ["\\mu_{0} = 4\\pi \\times 10^{-7}\\ \\text{T m A}^{-1}", "Permeability of free space"],
      ["\\frac{1}{4\\pi\\varepsilon_{0}} = 9 \\times 10^{9}\\ \\text{N m}^{2}\\text{C}^{-2}", "Coulomb constant"],
      ["\\sigma = 5.67 \\times 10^{-8}\\ \\text{W m}^{-2}\\text{K}^{-4}", "Stefan–Boltzmann constant"],
      ["b = 2.9 \\times 10^{-3}\\ \\text{m K}", "Wien's constant"],
      ["R_{H} = 1.097 \\times 10^{7}\\ \\text{m}^{-1}", "Rydberg constant"],
      ["a_{0} = 0.529\\ \\text{Å}", "Bohr radius"],
      ["1\\ \\text{eV} = 1.6 \\times 10^{-19}\\ \\text{J}", "Electron volt"],
      ["1\\ \\text{u} = 931.5\\ \\text{MeV}/c^{2}", "Atomic mass unit"],
      ["c", "Speed of light (symbol)"],
      ["h", "Planck's constant (symbol)"],
      ["G", "Gravitational constant (symbol)"],
      ["k_{B}", "Boltzmann constant (symbol)"],
      ["N_{A}", "Avogadro's number (symbol)"],
      ["\\varepsilon_{0}", "Permittivity (symbol)"],
      ["\\mu_{0}", "Permeability (symbol)"],
      ["\\frac{1}{4\\pi\\varepsilon_{0}}", "Coulomb constant (symbol)"],
    ],
  },
  {
    title: "Units",
    symbols: [
      ["\\text{m}", "Metre"],
      ["\\text{kg}", "Kilogram"],
      ["\\text{s}", "Second"],
      ["\\text{A}", "Ampere"],
      ["\\text{K}", "Kelvin"],
      ["\\text{mol}", "Mole"],
      ["\\text{cd}", "Candela"],
      ["\\text{N}", "Newton"],
      ["\\text{J}", "Joule"],
      ["\\text{W}", "Watt"],
      ["\\text{Pa}", "Pascal"],
      ["\\text{Hz}", "Hertz"],
      ["\\text{C}", "Coulomb"],
      ["\\text{V}", "Volt"],
      ["\\Omega", "Ohm"],
      ["\\text{k}\\Omega", "Kilo-ohm"],
      ["\\text{M}\\Omega", "Mega-ohm"],
      ["\\text{S}", "Siemens"],
      ["\\text{F}", "Farad"],
      ["\\mu\\text{F}", "Microfarad"],
      ["\\text{H}", "Henry"],
      ["\\text{T}", "Tesla"],
      ["\\text{Wb}", "Weber"],
      ["\\text{G}", "Gauss"],
      ["\\text{eV}", "Electron volt"],
      ["\\text{keV}", "Kilo electron volt"],
      ["\\text{MeV}", "Mega electron volt"],
      ["\\text{u}", "Atomic mass unit"],
      ["\\text{Bq}", "Becquerel"],
      ["\\text{Ci}", "Curie"],
      ["\\text{rad}", "Radian"],
      ["\\text{sr}", "Steradian"],
      ["\\text{dB}", "Decibel"],
      ["\\text{D}", "Dioptre"],
      ["\\text{Å}", "Angstrom"],
      ["\\text{nm}", "Nanometre"],
      ["\\mu\\text{m}", "Micrometre"],
      ["\\text{mm}", "Millimetre"],
      ["\\text{cm}", "Centimetre"],
      ["\\text{km}", "Kilometre"],
      ["\\text{g}", "Gram"],
      ["\\text{mg}", "Milligram"],
      ["\\text{L}", "Litre"],
      ["\\text{mL}", "Millilitre"],
      ["\\text{min}", "Minute"],
      ["\\text{h}", "Hour"],
      ["\\text{atm}", "Atmosphere"],
      ["\\text{bar}", "Bar"],
      ["\\text{mm of Hg}", "Millimetre of mercury"],
      ["\\text{cal}", "Calorie"],
      ["\\text{kWh}", "Kilowatt hour"],
      ["\\text{hp}", "Horsepower"],
      ["\\text{ly}", "Light year"],
      ["\\text{AU}", "Astronomical unit"],
      ["\\text{pc}", "Parsec"],
      ["^{\\circ}\\text{C}", "Degree Celsius"],
      ["^{\\circ}\\text{F}", "Degree Fahrenheit"],
      ["\\text{m s}^{-1}", "Metre per second"],
      ["\\text{m s}^{-2}", "Metre per second squared"],
      ["\\text{km h}^{-1}", "Kilometre per hour"],
      ["\\text{rad s}^{-1}", "Radian per second"],
      ["\\text{kg m}^{-3}", "Kilogram per cubic metre"],
      ["\\text{N m}^{-2}", "Newton per square metre"],
      ["\\text{N m}", "Newton metre"],
      ["\\text{kg m}^{2}", "Kilogram metre squared"],
      ["\\text{kg m s}^{-1}", "Momentum unit"],
      ["\\text{J kg}^{-1}\\text{K}^{-1}", "Specific heat unit"],
      ["\\text{W m}^{-1}\\text{K}^{-1}", "Thermal conductivity unit"],
      ["\\text{V m}^{-1}", "Volt per metre"],
      ["\\text{N C}^{-1}", "Newton per coulomb"],
      ["\\Omega\\,\\text{m}", "Ohm metre"],
      ["\\text{A m}^{2}", "Ampere metre squared"],
    ],
  },
  {
    title: "SI prefixes",
    symbols: [
      ["10^{-18}\\ (\\text{a})", "Atto"],
      ["10^{-15}\\ (\\text{f})", "Femto"],
      ["10^{-12}\\ (\\text{p})", "Pico"],
      ["10^{-9}\\ (\\text{n})", "Nano"],
      ["10^{-6}\\ (\\mu)", "Micro"],
      ["10^{-3}\\ (\\text{m})", "Milli"],
      ["10^{-2}\\ (\\text{c})", "Centi"],
      ["10^{3}\\ (\\text{k})", "Kilo"],
      ["10^{6}\\ (\\text{M})", "Mega"],
      ["10^{9}\\ (\\text{G})", "Giga"],
      ["10^{12}\\ (\\text{T})", "Tera"],
      ["10^{15}\\ (\\text{P})", "Peta"],
      ["a \\times 10^{n}", "Scientific notation"],
    ],
  },
  {
    title: "Dimensions & errors",
    symbols: [
      ["[\\text{M}]", "Mass dimension"],
      ["[\\text{L}]", "Length dimension"],
      ["[\\text{T}]", "Time dimension"],
      ["[\\text{A}]", "Current dimension"],
      ["[\\text{K}]", "Temperature dimension"],
      ["[\\text{M}^{a}\\text{L}^{b}\\text{T}^{c}]", "Dimensional formula"],
      ["[\\text{M}^{0}\\text{L}^{0}\\text{T}^{0}]", "Dimensionless"],
      ["[\\text{M}\\text{L}\\text{T}^{-2}]", "Force dimensions"],
      ["[\\text{M}\\text{L}^{2}\\text{T}^{-2}]", "Energy dimensions"],
      ["[\\text{M}\\text{L}^{2}\\text{T}^{-3}]", "Power dimensions"],
      ["[\\text{M}\\text{L}^{-1}\\text{T}^{-2}]", "Pressure dimensions"],
      ["[\\text{M}\\text{L}\\text{T}^{-1}]", "Momentum dimensions"],
      ["[\\text{M}^{-1}\\text{L}^{3}\\text{T}^{-2}]", "G dimensions"],
      ["[\\text{M}\\text{L}^{2}\\text{T}^{-1}]", "Planck constant dimensions"],
      ["\\Delta a", "Absolute error"],
      ["\\frac{\\Delta a}{a}", "Relative error"],
      ["\\frac{\\Delta a}{a} \\times 100\\%", "Percentage error"],
      ["\\frac{\\Delta Z}{Z} = \\frac{\\Delta A}{A} + \\frac{\\Delta B}{B}", "Error in product"],
      ["\\frac{\\Delta Z}{Z} = n\\frac{\\Delta A}{A}", "Error in power"],
      ["\\Delta Z = \\Delta A + \\Delta B", "Error in sum"],
      ["\\text{L.C.} = \\frac{\\text{pitch}}{\\text{no. of divisions}}", "Least count (screw gauge)"],
      ["\\text{L.C.} = 1\\ \\text{MSD} - 1\\ \\text{VSD}", "Least count (vernier)"],
    ],
  },
  {
    title: "Kinematics",
    symbols: [
      ["v = u + at", "First equation of motion"],
      ["s = ut + \\frac{1}{2}at^{2}", "Second equation of motion"],
      ["v^{2} = u^{2} + 2as", "Third equation of motion"],
      ["s_{n} = u + \\frac{a}{2}(2n - 1)", "Distance in nth second"],
      ["s = \\frac{(u + v)}{2}t", "Distance with average velocity"],
      ["\\vec{v} = \\frac{d\\vec{r}}{dt}", "Velocity (derivative)"],
      ["\\vec{a} = \\frac{d\\vec{v}}{dt}", "Acceleration (derivative)"],
      ["a = v\\frac{dv}{dx}", "Acceleration v dv/dx"],
      ["v_{\\text{avg}} = \\frac{\\Delta x}{\\Delta t}", "Average velocity"],
      ["\\vec{v}_{AB} = \\vec{v}_{A} - \\vec{v}_{B}", "Relative velocity"],
      ["T = \\frac{2u\\sin\\theta}{g}", "Time of flight"],
      ["H = \\frac{u^{2}\\sin^{2}\\theta}{2g}", "Maximum height"],
      ["R = \\frac{u^{2}\\sin 2\\theta}{g}", "Horizontal range"],
      ["y = x\\tan\\theta - \\frac{gx^{2}}{2u^{2}\\cos^{2}\\theta}", "Trajectory equation"],
      ["R_{\\max} = \\frac{u^{2}}{g}", "Maximum range"],
      ["\\omega = \\frac{d\\theta}{dt}", "Angular velocity"],
      ["v = r\\omega", "Linear and angular speed"],
      ["a_{c} = \\frac{v^{2}}{r} = \\omega^{2}r", "Centripetal acceleration"],
      ["\\omega = \\frac{2\\pi}{T} = 2\\pi f", "Angular speed from period"],
    ],
  },
  {
    title: "Laws of motion & friction",
    symbols: [
      ["\\vec{F} = m\\vec{a}", "Newton's second law"],
      ["\\vec{F} = \\frac{d\\vec{p}}{dt}", "Force as rate of momentum"],
      ["\\vec{p} = m\\vec{v}", "Momentum"],
      ["\\vec{J} = \\vec{F}\\Delta t = \\Delta\\vec{p}", "Impulse"],
      ["\\vec{F}_{12} = -\\vec{F}_{21}", "Newton's third law"],
      ["m_{1}u_{1} + m_{2}u_{2} = m_{1}v_{1} + m_{2}v_{2}", "Conservation of momentum"],
      ["W = mg", "Weight"],
      ["N", "Normal reaction"],
      ["T", "Tension"],
      ["f_{s} \\leq \\mu_{s}N", "Static friction"],
      ["f_{k} = \\mu_{k}N", "Kinetic friction"],
      ["\\mu_{s}", "Coefficient of static friction"],
      ["\\mu_{k}", "Coefficient of kinetic friction"],
      ["\\tan\\theta = \\mu", "Angle of repose"],
      ["F = \\frac{mv^{2}}{r}", "Centripetal force"],
      ["\\tan\\theta = \\frac{v^{2}}{rg}", "Banking of roads"],
      ["v_{\\max} = \\sqrt{\\mu rg}", "Maximum speed on a curve"],
      ["a = \\frac{(m_{1} - m_{2})g}{m_{1} + m_{2}}", "Atwood machine acceleration"],
      ["F = kx", "Spring force (magnitude)"],
      ["\\vec{F} = -k\\vec{x}", "Hooke's law"],
      ["F_{\\text{pseudo}} = -m\\vec{a}_{0}", "Pseudo force"],
    ],
  },
  {
    title: "Work, energy & power",
    symbols: [
      ["W = \\vec{F} \\cdot \\vec{s}", "Work (dot product)"],
      ["W = Fs\\cos\\theta", "Work done"],
      ["W = \\int \\vec{F} \\cdot d\\vec{s}", "Work (integral)"],
      ["K = \\frac{1}{2}mv^{2}", "Kinetic energy"],
      ["K = \\frac{p^{2}}{2m}", "KE and momentum"],
      ["U = mgh", "Gravitational PE"],
      ["U = \\frac{1}{2}kx^{2}", "Spring PE"],
      ["W_{\\text{net}} = \\Delta K", "Work–energy theorem"],
      ["F = -\\frac{dU}{dx}", "Force from potential"],
      ["K_{1} + U_{1} = K_{2} + U_{2}", "Conservation of energy"],
      ["P = \\frac{dW}{dt}", "Power"],
      ["P = \\vec{F} \\cdot \\vec{v}", "Power (force × velocity)"],
      ["\\eta = \\frac{\\text{output}}{\\text{input}} \\times 100\\%", "Efficiency"],
      ["e = \\frac{v_{2} - v_{1}}{u_{1} - u_{2}}", "Coefficient of restitution"],
      ["v = \\sqrt{5gl}", "Minimum speed at bottom (vertical circle)"],
      ["v = \\sqrt{gl}", "Minimum speed at top (vertical circle)"],
    ],
  },
  {
    title: "Rotational motion",
    symbols: [
      ["\\vec{\\tau} = \\vec{r} \\times \\vec{F}", "Torque"],
      ["\\tau = rF\\sin\\theta", "Torque magnitude"],
      ["\\vec{L} = \\vec{r} \\times \\vec{p}", "Angular momentum"],
      ["L = I\\omega", "Angular momentum (I omega)"],
      ["\\tau = I\\alpha", "Rotational second law"],
      ["\\vec{\\tau} = \\frac{d\\vec{L}}{dt}", "Torque as rate of L"],
      ["I_{1}\\omega_{1} = I_{2}\\omega_{2}", "Conservation of angular momentum"],
      ["I = \\sum m_{i}r_{i}^{2}", "Moment of inertia (sum)"],
      ["I = \\int r^{2}\\,dm", "Moment of inertia (integral)"],
      ["I = I_{\\text{cm}} + Md^{2}", "Parallel axis theorem"],
      ["I_{z} = I_{x} + I_{y}", "Perpendicular axis theorem"],
      ["I = Mk^{2}", "Radius of gyration"],
      ["K = \\frac{1}{2}I\\omega^{2}", "Rotational KE"],
      ["K = \\frac{1}{2}mv^{2}\\left(1 + \\frac{k^{2}}{R^{2}}\\right)", "Rolling KE"],
      ["v_{\\text{cm}} = R\\omega", "Rolling without slipping"],
      ["a = \\frac{g\\sin\\theta}{1 + \\frac{k^{2}}{R^{2}}}", "Rolling down incline"],
      ["\\vec{r}_{\\text{cm}} = \\frac{\\sum m_{i}\\vec{r}_{i}}{\\sum m_{i}}", "Centre of mass"],
      ["P = \\tau\\omega", "Rotational power"],
      ["\\omega = \\omega_{0} + \\alpha t", "Rotational equation 1"],
      ["\\theta = \\omega_{0}t + \\frac{1}{2}\\alpha t^{2}", "Rotational equation 2"],
      ["\\omega^{2} = \\omega_{0}^{2} + 2\\alpha\\theta", "Rotational equation 3"],
      ["\\frac{1}{2}MR^{2}", "MI of disc / cylinder"],
      ["MR^{2}", "MI of ring"],
      ["\\frac{2}{5}MR^{2}", "MI of solid sphere"],
      ["\\frac{2}{3}MR^{2}", "MI of hollow sphere"],
      ["\\frac{1}{12}ML^{2}", "MI of rod (centre)"],
      ["\\frac{1}{3}ML^{2}", "MI of rod (end)"],
    ],
  },
  {
    title: "Gravitation",
    symbols: [
      ["F = \\frac{Gm_{1}m_{2}}{r^{2}}", "Newton's law of gravitation"],
      ["g = \\frac{GM}{R^{2}}", "Acceleration due to gravity"],
      ["g_{h} = g\\left(1 - \\frac{2h}{R}\\right)", "g at height"],
      ["g_{d} = g\\left(1 - \\frac{d}{R}\\right)", "g at depth"],
      ["g' = g - \\omega^{2}R\\cos^{2}\\lambda", "g with latitude"],
      ["U = -\\frac{GMm}{r}", "Gravitational PE"],
      ["V = -\\frac{GM}{r}", "Gravitational potential"],
      ["E = \\frac{GM}{r^{2}}", "Gravitational field"],
      ["v_{e} = \\sqrt{2gR} = \\sqrt{\\frac{2GM}{R}}", "Escape velocity"],
      ["v_{o} = \\sqrt{\\frac{GM}{r}}", "Orbital velocity"],
      ["T = 2\\pi\\sqrt{\\frac{r^{3}}{GM}}", "Time period of satellite"],
      ["T^{2} \\propto r^{3}", "Kepler's third law"],
      ["\\frac{dA}{dt} = \\frac{L}{2m}", "Areal velocity"],
      ["E = -\\frac{GMm}{2r}", "Total energy of satellite"],
    ],
  },
  {
    title: "Properties of matter & fluids",
    symbols: [
      ["\\text{Stress} = \\frac{F}{A}", "Stress"],
      ["\\text{Strain} = \\frac{\\Delta L}{L}", "Strain"],
      ["Y = \\frac{F/A}{\\Delta L/L}", "Young's modulus"],
      ["B = -\\frac{\\Delta P}{\\Delta V/V}", "Bulk modulus"],
      ["\\eta = \\frac{F/A}{\\theta}", "Modulus of rigidity"],
      ["\\sigma = -\\frac{\\Delta D/D}{\\Delta L/L}", "Poisson's ratio"],
      ["U = \\frac{1}{2} \\times \\text{stress} \\times \\text{strain} \\times \\text{volume}", "Elastic PE"],
      ["\\rho = \\frac{m}{V}", "Density"],
      ["P = \\frac{F}{A}", "Pressure"],
      ["P = P_{0} + \\rho gh", "Pressure at depth"],
      ["F_{B} = \\rho Vg", "Buoyant force"],
      ["A_{1}v_{1} = A_{2}v_{2}", "Equation of continuity"],
      ["P + \\frac{1}{2}\\rho v^{2} + \\rho gh = \\text{constant}", "Bernoulli's equation"],
      ["v = \\sqrt{2gh}", "Torricelli's law"],
      ["F = -\\eta A\\frac{dv}{dx}", "Viscous force"],
      ["F = 6\\pi\\eta rv", "Stokes' law"],
      ["v_{t} = \\frac{2r^{2}(\\rho - \\sigma)g}{9\\eta}", "Terminal velocity"],
      ["R_{e} = \\frac{\\rho vD}{\\eta}", "Reynolds number"],
      ["S = \\frac{F}{l}", "Surface tension"],
      ["h = \\frac{2S\\cos\\theta}{r\\rho g}", "Capillary rise"],
      ["\\Delta P = \\frac{2S}{R}", "Excess pressure (drop)"],
      ["\\Delta P = \\frac{4S}{R}", "Excess pressure (bubble)"],
    ],
  },
  {
    title: "Heat & thermodynamics",
    symbols: [
      ["Q = mc\\Delta T", "Heat and specific heat"],
      ["Q = mL", "Latent heat"],
      ["\\Delta L = L\\alpha\\Delta T", "Linear expansion"],
      ["\\Delta V = V\\gamma\\Delta T", "Volume expansion"],
      ["\\beta = 2\\alpha,\\ \\gamma = 3\\alpha", "Expansion coefficients"],
      ["T(\\text{K}) = T(^{\\circ}\\text{C}) + 273.15", "Kelvin conversion"],
      ["\\frac{C}{5} = \\frac{F - 32}{9}", "Celsius–Fahrenheit"],
      ["PV = nRT", "Ideal gas equation"],
      ["PV = Nk_{B}T", "Ideal gas (molecules)"],
      ["P = \\frac{1}{3}\\rho v_{\\text{rms}}^{2}", "Kinetic theory pressure"],
      ["v_{\\text{rms}} = \\sqrt{\\frac{3RT}{M}}", "RMS speed"],
      ["v_{\\text{avg}} = \\sqrt{\\frac{8RT}{\\pi M}}", "Average speed"],
      ["v_{\\text{mp}} = \\sqrt{\\frac{2RT}{M}}", "Most probable speed"],
      ["\\frac{3}{2}k_{B}T", "Average KE per molecule"],
      ["\\frac{f}{2}k_{B}T", "Equipartition of energy"],
      ["\\Delta Q = \\Delta U + \\Delta W", "First law of thermodynamics"],
      ["\\Delta U = nC_{v}\\Delta T", "Change in internal energy"],
      ["W = \\int P\\,dV", "Work done by gas"],
      ["W = nRT\\ln\\frac{V_{2}}{V_{1}}", "Isothermal work"],
      ["W = \\frac{nR(T_{1} - T_{2})}{\\gamma - 1}", "Adiabatic work"],
      ["PV^{\\gamma} = \\text{constant}", "Adiabatic process"],
      ["TV^{\\gamma - 1} = \\text{constant}", "Adiabatic (T–V)"],
      ["\\gamma = \\frac{C_{p}}{C_{v}}", "Ratio of specific heats"],
      ["C_{p} - C_{v} = R", "Mayer's relation"],
      ["\\eta = 1 - \\frac{T_{2}}{T_{1}}", "Carnot efficiency"],
      ["\\eta = \\frac{W}{Q_{1}}", "Heat engine efficiency"],
      ["\\beta = \\frac{Q_{2}}{W} = \\frac{T_{2}}{T_{1} - T_{2}}", "Refrigerator COP"],
      ["\\Delta S = \\frac{\\Delta Q}{T}", "Entropy change"],
      ["\\frac{dQ}{dt} = \\frac{KA(T_{1} - T_{2})}{L}", "Heat conduction"],
      ["R_{th} = \\frac{L}{KA}", "Thermal resistance"],
      ["E = \\sigma T^{4}", "Stefan–Boltzmann law"],
      ["E = e\\sigma AT^{4}", "Radiated power"],
      ["\\lambda_{m}T = b", "Wien's displacement law"],
      ["\\frac{dT}{dt} = -k(T - T_{0})", "Newton's law of cooling"],
    ],
  },
  {
    title: "Oscillations & waves",
    symbols: [
      ["x = A\\sin(\\omega t + \\phi)", "SHM displacement"],
      ["v = \\omega\\sqrt{A^{2} - x^{2}}", "SHM velocity"],
      ["a = -\\omega^{2}x", "SHM acceleration"],
      ["\\frac{d^{2}x}{dt^{2}} + \\omega^{2}x = 0", "SHM differential equation"],
      ["T = 2\\pi\\sqrt{\\frac{m}{k}}", "Spring–mass period"],
      ["T = 2\\pi\\sqrt{\\frac{l}{g}}", "Simple pendulum period"],
      ["T = 2\\pi\\sqrt{\\frac{I}{mgd}}", "Physical pendulum period"],
      ["E = \\frac{1}{2}m\\omega^{2}A^{2}", "Energy in SHM"],
      ["k_{s} = k_{1} + k_{2}", "Springs in parallel"],
      ["\\frac{1}{k_{s}} = \\frac{1}{k_{1}} + \\frac{1}{k_{2}}", "Springs in series"],
      ["f = \\frac{1}{T}", "Frequency and period"],
      ["v = f\\lambda", "Wave speed"],
      ["y = A\\sin(kx - \\omega t)", "Travelling wave"],
      ["y = 2A\\sin kx\\cos\\omega t", "Standing wave"],
      ["k = \\frac{2\\pi}{\\lambda}", "Wave number"],
      ["v = \\sqrt{\\frac{T}{\\mu}}", "Speed on a string"],
      ["v = \\sqrt{\\frac{\\gamma P}{\\rho}}", "Speed of sound in gas"],
      ["v = \\sqrt{\\frac{Y}{\\rho}}", "Speed of sound in solid"],
      ["f_{n} = \\frac{nv}{2L}", "String / open pipe harmonics"],
      ["f_{n} = \\frac{(2n - 1)v}{4L}", "Closed pipe harmonics"],
      ["f_{\\text{beat}} = |f_{1} - f_{2}|", "Beat frequency"],
      ["f' = f\\left(\\frac{v \\pm v_{o}}{v \\mp v_{s}}\\right)", "Doppler effect"],
      ["\\beta = 10\\log_{10}\\frac{I}{I_{0}}", "Sound level (dB)"],
      ["I \\propto A^{2}", "Intensity and amplitude"],
      ["\\Delta\\phi = \\frac{2\\pi}{\\lambda}\\Delta x", "Phase difference"],
    ],
  },
  {
    title: "Electrostatics",
    symbols: [
      ["F = \\frac{1}{4\\pi\\varepsilon_{0}}\\frac{q_{1}q_{2}}{r^{2}}", "Coulomb's law"],
      ["F = \\frac{kq_{1}q_{2}}{r^{2}}", "Coulomb's law (k)"],
      ["\\vec{E} = \\frac{\\vec{F}}{q_{0}}", "Electric field"],
      ["E = \\frac{kq}{r^{2}}", "Field of point charge"],
      ["V = \\frac{kq}{r}", "Potential of point charge"],
      ["\\vec{E} = -\\nabla V", "Field from potential"],
      ["E = -\\frac{dV}{dr}", "Field as potential gradient"],
      ["U = \\frac{kq_{1}q_{2}}{r}", "Electric PE"],
      ["\\Phi_{E} = \\oint \\vec{E} \\cdot d\\vec{A}", "Electric flux"],
      ["\\oint \\vec{E} \\cdot d\\vec{A} = \\frac{q_{\\text{enc}}}{\\varepsilon_{0}}", "Gauss's law"],
      ["E = \\frac{\\lambda}{2\\pi\\varepsilon_{0}r}", "Field of line charge"],
      ["E = \\frac{\\sigma}{2\\varepsilon_{0}}", "Field of sheet"],
      ["E = \\frac{\\sigma}{\\varepsilon_{0}}", "Field near conductor"],
      ["E = \\frac{kQr}{R^{3}}", "Field inside solid sphere"],
      ["\\vec{p} = q\\vec{d}", "Dipole moment"],
      ["E = \\frac{2kp}{r^{3}}", "Dipole field (axial)"],
      ["E = \\frac{kp}{r^{3}}", "Dipole field (equatorial)"],
      ["\\vec{\\tau} = \\vec{p} \\times \\vec{E}", "Torque on dipole"],
      ["U = -\\vec{p} \\cdot \\vec{E}", "Dipole PE"],
      ["\\lambda", "Linear charge density"],
      ["\\sigma", "Surface charge density"],
      ["\\rho", "Volume charge density"],
      ["C = \\frac{Q}{V}", "Capacitance"],
      ["C = \\frac{\\varepsilon_{0}A}{d}", "Parallel plate capacitor"],
      ["C = \\frac{K\\varepsilon_{0}A}{d}", "Capacitor with dielectric"],
      ["C = 4\\pi\\varepsilon_{0}R", "Spherical conductor capacitance"],
      ["U = \\frac{1}{2}CV^{2} = \\frac{Q^{2}}{2C}", "Energy in capacitor"],
      ["u = \\frac{1}{2}\\varepsilon_{0}E^{2}", "Energy density"],
      ["C_{p} = C_{1} + C_{2}", "Capacitors in parallel"],
      ["\\frac{1}{C_{s}} = \\frac{1}{C_{1}} + \\frac{1}{C_{2}}", "Capacitors in series"],
      ["K = \\frac{\\varepsilon}{\\varepsilon_{0}}", "Dielectric constant"],
    ],
  },
  {
    title: "Current electricity",
    symbols: [
      ["I = \\frac{dq}{dt}", "Current"],
      ["V = IR", "Ohm's law"],
      ["R = \\rho\\frac{l}{A}", "Resistance"],
      ["\\sigma = \\frac{1}{\\rho}", "Conductivity"],
      ["\\vec{J} = \\sigma\\vec{E}", "Current density"],
      ["I = neAv_{d}", "Current and drift velocity"],
      ["v_{d} = \\frac{eE\\tau}{m}", "Drift velocity"],
      ["\\mu = \\frac{v_{d}}{E}", "Mobility"],
      ["R_{t} = R_{0}(1 + \\alpha\\Delta T)", "Temperature dependence"],
      ["R_{s} = R_{1} + R_{2} + R_{3}", "Resistors in series"],
      ["\\frac{1}{R_{p}} = \\frac{1}{R_{1}} + \\frac{1}{R_{2}}", "Resistors in parallel"],
      ["V = \\varepsilon - Ir", "Terminal voltage"],
      ["I = \\frac{\\varepsilon}{R + r}", "Current with internal resistance"],
      ["P = VI = I^{2}R = \\frac{V^{2}}{R}", "Electric power"],
      ["H = I^{2}Rt", "Joule heating"],
      ["\\sum I = 0", "Kirchhoff's junction rule"],
      ["\\sum \\Delta V = 0", "Kirchhoff's loop rule"],
      ["\\frac{P}{Q} = \\frac{R}{S}", "Wheatstone bridge"],
      ["\\frac{R}{S} = \\frac{l}{100 - l}", "Metre bridge"],
      ["\\frac{\\varepsilon_{1}}{\\varepsilon_{2}} = \\frac{l_{1}}{l_{2}}", "Potentiometer comparison"],
      ["r = R\\left(\\frac{l_{1}}{l_{2}} - 1\\right)", "Internal resistance (potentiometer)"],
      ["S = \\frac{I_{g}G}{I - I_{g}}", "Shunt (ammeter)"],
      ["R = \\frac{V}{I_{g}} - G", "Series resistance (voltmeter)"],
      ["\\varepsilon_{eq} = \\frac{\\varepsilon_{1}r_{2} + \\varepsilon_{2}r_{1}}{r_{1} + r_{2}}", "Cells in parallel"],
    ],
  },
  {
    title: "Magnetism & EMI",
    symbols: [
      ["\\vec{F} = q(\\vec{v} \\times \\vec{B})", "Magnetic force on charge"],
      ["\\vec{F} = q(\\vec{E} + \\vec{v} \\times \\vec{B})", "Lorentz force"],
      ["\\vec{F} = I\\vec{L} \\times \\vec{B}", "Force on current"],
      ["r = \\frac{mv}{qB}", "Radius in magnetic field"],
      ["T = \\frac{2\\pi m}{qB}", "Cyclotron period"],
      ["f = \\frac{qB}{2\\pi m}", "Cyclotron frequency"],
      ["d\\vec{B} = \\frac{\\mu_{0}}{4\\pi}\\frac{I\\,d\\vec{l} \\times \\hat{r}}{r^{2}}", "Biot–Savart law"],
      ["B = \\frac{\\mu_{0}I}{2\\pi r}", "Field of long wire"],
      ["B = \\frac{\\mu_{0}I}{2R}", "Field at centre of loop"],
      ["B = \\frac{\\mu_{0}NIR^{2}}{2(R^{2} + x^{2})^{3/2}}", "Field on axis of loop"],
      ["B = \\mu_{0}nI", "Field in solenoid"],
      ["B = \\frac{\\mu_{0}NI}{2\\pi r}", "Field in toroid"],
      ["\\oint \\vec{B} \\cdot d\\vec{l} = \\mu_{0}I_{\\text{enc}}", "Ampère's law"],
      ["\\frac{F}{l} = \\frac{\\mu_{0}I_{1}I_{2}}{2\\pi d}", "Force between wires"],
      ["\\vec{\\mu} = NI\\vec{A}", "Magnetic moment"],
      ["\\vec{\\tau} = \\vec{\\mu} \\times \\vec{B}", "Torque on loop"],
      ["U = -\\vec{\\mu} \\cdot \\vec{B}", "Magnetic PE"],
      ["\\vec{B} = \\mu_{0}(\\vec{H} + \\vec{M})", "B, H and M"],
      ["\\chi = \\frac{M}{H}", "Magnetic susceptibility"],
      ["\\mu_{r} = 1 + \\chi", "Relative permeability"],
      ["\\Phi_{B} = \\vec{B} \\cdot \\vec{A}", "Magnetic flux"],
      ["\\varepsilon = -\\frac{d\\Phi_{B}}{dt}", "Faraday's law"],
      ["\\varepsilon = -N\\frac{d\\Phi}{dt}", "Faraday's law (N turns)"],
      ["\\varepsilon = Blv", "Motional EMF"],
      ["\\varepsilon = \\frac{1}{2}B\\omega l^{2}", "EMF in rotating rod"],
      ["\\varepsilon = NBA\\omega\\sin\\omega t", "AC generator EMF"],
      ["\\varepsilon = -L\\frac{dI}{dt}", "Self-induced EMF"],
      ["\\varepsilon_{2} = -M\\frac{dI_{1}}{dt}", "Mutual induction"],
      ["L = \\mu_{0}n^{2}Al", "Inductance of solenoid"],
      ["U = \\frac{1}{2}LI^{2}", "Energy in inductor"],
      ["u_{B} = \\frac{B^{2}}{2\\mu_{0}}", "Magnetic energy density"],
      ["\\tau = \\frac{L}{R}", "LR time constant"],
      ["\\tau = RC", "RC time constant"],
      ["I = I_{0}\\left(1 - e^{-t/\\tau}\\right)", "Growth of current"],
      ["q = q_{0}e^{-t/RC}", "Capacitor discharge"],
    ],
  },
  {
    title: "Alternating current & EM waves",
    symbols: [
      ["V = V_{0}\\sin\\omega t", "AC voltage"],
      ["I = I_{0}\\sin(\\omega t + \\phi)", "AC current"],
      ["V_{\\text{rms}} = \\frac{V_{0}}{\\sqrt{2}}", "RMS voltage"],
      ["I_{\\text{rms}} = \\frac{I_{0}}{\\sqrt{2}}", "RMS current"],
      ["X_{L} = \\omega L", "Inductive reactance"],
      ["X_{C} = \\frac{1}{\\omega C}", "Capacitive reactance"],
      ["Z = \\sqrt{R^{2} + (X_{L} - X_{C})^{2}}", "Impedance"],
      ["\\tan\\phi = \\frac{X_{L} - X_{C}}{R}", "Phase angle"],
      ["\\omega_{0} = \\frac{1}{\\sqrt{LC}}", "Resonant frequency"],
      ["f_{0} = \\frac{1}{2\\pi\\sqrt{LC}}", "Resonant frequency (Hz)"],
      ["Q = \\frac{\\omega_{0}L}{R}", "Quality factor"],
      ["P = V_{\\text{rms}}I_{\\text{rms}}\\cos\\phi", "Average power"],
      ["\\cos\\phi = \\frac{R}{Z}", "Power factor"],
      ["\\frac{V_{s}}{V_{p}} = \\frac{N_{s}}{N_{p}} = \\frac{I_{p}}{I_{s}}", "Transformer ratio"],
      ["I_{d} = \\varepsilon_{0}\\frac{d\\Phi_{E}}{dt}", "Displacement current"],
      ["c = \\frac{1}{\\sqrt{\\mu_{0}\\varepsilon_{0}}}", "Speed of EM waves"],
      ["E_{0} = cB_{0}", "E and B amplitudes"],
      ["\\vec{S} = \\frac{1}{\\mu_{0}}\\vec{E} \\times \\vec{B}", "Poynting vector"],
      ["I = \\frac{1}{2}\\varepsilon_{0}E_{0}^{2}c", "EM wave intensity"],
      ["p = \\frac{U}{c}", "Radiation momentum"],
    ],
  },
  {
    title: "Ray optics",
    symbols: [
      ["\\frac{1}{f} = \\frac{1}{v} + \\frac{1}{u}", "Mirror formula"],
      ["\\frac{1}{f} = \\frac{1}{v} - \\frac{1}{u}", "Lens formula"],
      ["m = -\\frac{v}{u}", "Magnification (mirror)"],
      ["m = \\frac{v}{u}", "Magnification (lens)"],
      ["m = \\frac{h'}{h}", "Magnification (heights)"],
      ["f = \\frac{R}{2}", "Focal length of mirror"],
      ["n = \\frac{c}{v}", "Refractive index"],
      ["n_{1}\\sin\\theta_{1} = n_{2}\\sin\\theta_{2}", "Snell's law"],
      ["{}_{1}n_{2} = \\frac{n_{2}}{n_{1}}", "Relative refractive index"],
      ["\\sin C = \\frac{1}{n}", "Critical angle"],
      ["n = \\frac{\\text{real depth}}{\\text{apparent depth}}", "Real and apparent depth"],
      ["\\frac{n_{2}}{v} - \\frac{n_{1}}{u} = \\frac{n_{2} - n_{1}}{R}", "Refraction at curved surface"],
      ["\\frac{1}{f} = (n - 1)\\left(\\frac{1}{R_{1}} - \\frac{1}{R_{2}}\\right)", "Lens maker's formula"],
      ["P = \\frac{1}{f}", "Power of lens"],
      ["\\frac{1}{F} = \\frac{1}{f_{1}} + \\frac{1}{f_{2}}", "Lenses in contact"],
      ["n = \\frac{\\sin\\frac{A + \\delta_{m}}{2}}{\\sin\\frac{A}{2}}", "Prism formula"],
      ["\\delta = (n - 1)A", "Deviation (thin prism)"],
      ["A + \\delta = i + e", "Prism angles"],
      ["\\omega = \\frac{n_{v} - n_{r}}{n_{y} - 1}", "Dispersive power"],
      ["m = 1 + \\frac{D}{f}", "Simple microscope"],
      ["m = \\frac{v_{o}}{u_{o}}\\left(1 + \\frac{D}{f_{e}}\\right)", "Compound microscope"],
      ["m = \\frac{f_{o}}{f_{e}}", "Telescope magnification"],
      ["L = f_{o} + f_{e}", "Telescope length"],
    ],
  },
  {
    title: "Wave optics",
    symbols: [
      ["\\Delta x = d\\sin\\theta", "Path difference"],
      ["\\Delta x = n\\lambda", "Constructive interference"],
      ["\\Delta x = (2n - 1)\\frac{\\lambda}{2}", "Destructive interference"],
      ["\\beta = \\frac{\\lambda D}{d}", "Fringe width"],
      ["y_{n} = \\frac{n\\lambda D}{d}", "Position of nth bright fringe"],
      ["I = I_{1} + I_{2} + 2\\sqrt{I_{1}I_{2}}\\cos\\phi", "Resultant intensity"],
      ["I = 4I_{0}\\cos^{2}\\frac{\\phi}{2}", "Intensity (equal sources)"],
      ["\\frac{I_{\\max}}{I_{\\min}} = \\left(\\frac{\\sqrt{I_{1}} + \\sqrt{I_{2}}}{\\sqrt{I_{1}} - \\sqrt{I_{2}}}\\right)^{2}", "Intensity ratio"],
      ["a\\sin\\theta = n\\lambda", "Single slit minima"],
      ["\\frac{2\\lambda D}{a}", "Width of central maximum"],
      ["\\theta = \\frac{1.22\\lambda}{D}", "Resolving limit"],
      ["I = I_{0}\\cos^{2}\\theta", "Malus's law"],
      ["\\tan\\theta_{B} = n", "Brewster's law"],
      ["\\frac{\\Delta\\lambda}{\\lambda} = \\frac{v}{c}", "Doppler shift (light)"],
    ],
  },
  {
    title: "Modern physics",
    symbols: [
      ["E = h\\nu", "Photon energy"],
      ["E = \\frac{hc}{\\lambda}", "Photon energy (wavelength)"],
      ["E(\\text{eV}) = \\frac{1240}{\\lambda(\\text{nm})}", "Photon energy shortcut"],
      ["p = \\frac{h}{\\lambda}", "Photon momentum"],
      ["K_{\\max} = h\\nu - \\phi_{0}", "Photoelectric equation"],
      ["eV_{0} = h\\nu - \\phi_{0}", "Stopping potential"],
      ["\\phi_{0} = h\\nu_{0}", "Work function"],
      ["\\lambda = \\frac{h}{p} = \\frac{h}{mv}", "de Broglie wavelength"],
      ["\\lambda = \\frac{h}{\\sqrt{2mK}}", "de Broglie (kinetic energy)"],
      ["\\lambda = \\frac{12.27}{\\sqrt{V}}\\ \\text{Å}", "Electron wavelength"],
      ["\\Delta x\\,\\Delta p \\geq \\frac{h}{4\\pi}", "Uncertainty principle"],
      ["E = mc^{2}", "Mass–energy equivalence"],
      ["mvr = \\frac{nh}{2\\pi}", "Bohr quantisation"],
      ["r_{n} = 0.529\\frac{n^{2}}{Z}\\ \\text{Å}", "Bohr radius (nth orbit)"],
      ["v_{n} = 2.18 \\times 10^{6}\\frac{Z}{n}\\ \\text{m s}^{-1}", "Velocity in nth orbit"],
      ["E_{n} = -13.6\\frac{Z^{2}}{n^{2}}\\ \\text{eV}", "Energy of nth orbit"],
      ["\\frac{1}{\\lambda} = RZ^{2}\\left(\\frac{1}{n_{1}^{2}} - \\frac{1}{n_{2}^{2}}\\right)", "Rydberg formula"],
      ["\\lambda_{\\min} = \\frac{hc}{eV}", "Cut-off X-ray wavelength"],
      ["\\sqrt{\\nu} = a(Z - b)", "Moseley's law"],
      ["R = R_{0}A^{1/3}", "Nuclear radius"],
      ["\\Delta m = [Zm_{p} + (A - Z)m_{n}] - M", "Mass defect"],
      ["E_{b} = \\Delta m\\,c^{2}", "Binding energy"],
      ["N = N_{0}e^{-\\lambda t}", "Radioactive decay"],
      ["N = N_{0}\\left(\\frac{1}{2}\\right)^{t/T_{1/2}}", "Decay by half-lives"],
      ["A = \\lambda N", "Activity"],
      ["-\\frac{dN}{dt} = \\lambda N", "Decay rate"],
      ["T_{1/2} = \\frac{\\ln 2}{\\lambda} = \\frac{0.693}{\\lambda}", "Half-life"],
      ["\\tau = \\frac{1}{\\lambda}", "Mean life"],
      ["Q = (m_{\\text{reactants}} - m_{\\text{products}})c^{2}", "Q value"],
    ],
  },
  {
    title: "Nuclear symbols",
    symbols: [
      ["{}^{A}_{Z}\\text{X}", "Nuclide notation"],
      ["\\alpha", "Alpha particle"],
      ["\\beta^{-}", "Beta minus"],
      ["\\beta^{+}", "Beta plus (positron)"],
      ["\\gamma", "Gamma ray"],
      ["\\nu", "Neutrino"],
      ["\\bar{\\nu}", "Antineutrino"],
      ["{}^{4}_{2}\\text{He}", "Helium nucleus"],
      ["{}^{1}_{0}\\text{n}", "Neutron"],
      ["{}^{1}_{1}\\text{p}", "Proton"],
      ["{}^{1}_{1}\\text{H}", "Hydrogen"],
      ["{}^{2}_{1}\\text{H}", "Deuterium"],
      ["{}^{3}_{1}\\text{H}", "Tritium"],
      ["{}^{0}_{-1}\\text{e}", "Electron"],
      ["{}^{0}_{+1}\\text{e}", "Positron"],
      ["{}^{235}_{92}\\text{U}", "Uranium-235"],
      ["{}^{238}_{92}\\text{U}", "Uranium-238"],
      ["{}^{14}_{6}\\text{C}", "Carbon-14"],
      ["{}^{A}_{Z}\\text{X} \\rightarrow {}^{A-4}_{Z-2}\\text{Y} + {}^{4}_{2}\\text{He}", "Alpha decay"],
      ["{}^{A}_{Z}\\text{X} \\rightarrow {}^{A}_{Z+1}\\text{Y} + e^{-} + \\bar{\\nu}", "Beta-minus decay"],
      ["{}^{A}_{Z}\\text{X} \\rightarrow {}^{A}_{Z-1}\\text{Y} + e^{+} + \\nu", "Beta-plus decay"],
    ],
  },
  {
    title: "Semiconductors & logic gates",
    symbols: [
      ["n_{e}n_{h} = n_{i}^{2}", "Mass action law"],
      ["\\sigma = e(n_{e}\\mu_{e} + n_{h}\\mu_{h})", "Semiconductor conductivity"],
      ["I = I_{0}\\left(e^{eV/k_{B}T} - 1\\right)", "Diode equation"],
      ["I_{E} = I_{B} + I_{C}", "Transistor currents"],
      ["\\alpha = \\frac{I_{C}}{I_{E}}", "Current gain (CB)"],
      ["\\beta = \\frac{I_{C}}{I_{B}}", "Current gain (CE)"],
      ["\\beta = \\frac{\\alpha}{1 - \\alpha}", "Alpha–beta relation"],
      ["A_{v} = \\beta\\frac{R_{L}}{R_{i}}", "Voltage gain"],
      ["V_{BE}", "Base–emitter voltage"],
      ["V_{CE}", "Collector–emitter voltage"],
      ["\\overline{A}", "NOT gate"],
      ["A \\cdot B", "AND gate"],
      ["A + B", "OR gate"],
      ["\\overline{A \\cdot B}", "NAND gate"],
      ["\\overline{A + B}", "NOR gate"],
      ["A \\oplus B", "XOR gate"],
      ["\\overline{A \\oplus B}", "XNOR gate"],
      ["Y = A\\overline{B} + \\overline{A}B", "XOR expression"],
      ["\\overline{A + B} = \\overline{A} \\cdot \\overline{B}", "De Morgan's law 1"],
      ["\\overline{A \\cdot B} = \\overline{A} + \\overline{B}", "De Morgan's law 2"],
    ],
  },
];

export default physicsSymbols;
